'use client';

import { useState } from 'react';
import Icon from '@/components/ui/AppIcon';

interface FAQItem {
  question: string;
  answer: string;
  details: string[];
}

export default function PaymentsBillingSection() {
  const [openIndex, setOpenIndex] = useState<number | null>(null);

  const faqs: FAQItem[] = [
    {
      question: 'What payment methods do you accept?',
      answer: 'We accept all major payment methods including credit/debit cards, UPI, net banking, and corporate billing.',
      details: [
        'Credit Cards: Visa, Mastercard, American Express, Diners Club',
        'Debit Cards: All major Indian banks',
        'UPI: Google Pay, PhonePe, Paytm, BHIM',
        'Net Banking: All major Indian banks',
        'Bank Transfer: NEFT/RTGS for advance bookings',
        'Corporate Invoicing: Available for registered business accounts',
      ],
    },
    {
      question: 'When is payment required?',
      answer: 'Payment timing depends on booking type, with options for advance payment or pay-at-property.',
      details: [
        'Short Stays (1-6 nights): 100% payment at time of booking',
        'Weekly Stays (7-29 nights): 50% advance, balance 48 hours before check-in',
        'Monthly Bookings (30+ nights): 30% advance, balance on check-in',
        'Corporate Accounts: Net 30 payment terms available',
        'Last-minute bookings (within 48 hours): Full payment required',
      ],
    },
    {
      question: 'What is the security deposit amount?',
      answer: 'Refundable security deposit varies by apartment category and is authorized at check-in.',
      details: [
        'Luxos Luxury: ₹5,000 security deposit',
        'Primera Premium: ₹4,000 security deposit',
        'Classica Comfort: ₹3,000 security deposit',
        'Deposit held as credit card authorization (not charged)',
        'Released within 48 hours of check-out after inspection',
        'Deductions made only for damages or missing items',
        'Corporate accounts may have deposit waived',
      ],
    },
    {
      question: 'Are there any additional charges?',
      answer: 'Additional charges may apply for extra services, early/late check-in/out, and optional amenities.',
      details: [
        'Extra Guest: ₹1,000 per night (subject to occupancy limits)',
        'Airport Transfers: ₹1,500 one-way (sedan), ₹2,500 (SUV)',
        'Laundry Services: As per service menu pricing',
        'Late Check-out: 50% of daily rate (subject to availability)',
        'Pet Accommodation: ₹1,500 per night (pre-approval required)',
        'Parking: Complimentary for registered guests',
      ],
    },
    {
      question: 'How do refunds work?',
      answer: 'Refunds are processed based on cancellation policy and returned to original payment method within 7-10 business days.',
      details: [
        'Refund amount based on cancellation timeline',
        'Processed to original payment method',
        'Credit/debit card refunds: 7-10 business days',
        'UPI/Net banking refunds: 5-7 business days',
        'Processing fee of ₹500 applies to all cancellations with refunds',
        'Corporate accounts: Credit note issued for future bookings',
      ],
    },
    {
      question: 'Do you provide invoices for corporate expenses?',
      answer: 'Yes, we provide detailed GST-compliant invoices for all bookings, suitable for corporate reimbursement.',
      details: [
        'GST-compliant invoices issued for all transactions',
        'Invoices emailed within 24 hours of check-out',
        'Itemized breakdown of all charges',
        'Company name and GSTIN can be added to invoice',
        'Duplicate invoices available on request',
      ],
    },
  ];

  const toggleItem = (index: number) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  return (
    <section id="payments-billing" className="py-16 lg:py-24 bg-white">
      <div className="max-w-5xl mx-auto px-4 lg:px-8">
        <div className="text-center mb-12">
          <div className="inline-flex items-center space-x-2 text-accent mb-4">
            <Icon name="CreditCardIcon" size={24} />
            <span className="text-sm font-semibold uppercase tracking-wide">Payments & Billing</span>
          </div>
          <h2 className="font-display text-3xl lg:text-5xl font-bold text-primary mb-4">
            Payment Information
          </h2>
          <p className="text-lg text-text-secondary max-w-3xl mx-auto">
            Understand our payment methods, billing procedures, security deposits, and refund processes.
          </p>
        </div>

        <div className="space-y-4">
          {faqs.map((faq, index) => (
            <div
              key={index}
              className="bg-white rounded-xl shadow-subtle border border-border overflow-hidden hover:shadow-elevated transition-all duration-300"
            >
              <button
                onClick={() => toggleItem(index)}
                className="w-full px-6 py-5 flex items-center justify-between text-left hover:bg-muted transition-colors duration-300"
              >
                <div className="flex-1 pr-4">
                  <h3 className="font-display text-xl font-semibold text-primary mb-2">
                    {faq.question}
                  </h3>
                  <p className="text-text-secondary text-sm">{faq.answer}</p>
                </div>
                <Icon
                  name={openIndex === index ? 'ChevronUpIcon' : 'ChevronDownIcon'}
                  size={24}
                  className="text-text-secondary flex-shrink-0"
                />
              </button>

              {openIndex === index && (
                <div className="px-6 pb-6 animate-slide-in-left">
                  <div className="border-t border-border pt-4">
                    <ul className="space-y-3">
                      {faq.details.map((detail, idx) => (
                        <li key={idx} className="flex items-start space-x-3">
                          <Icon
                            name="CheckCircleIcon"
                            size={20}
                            className="text-accent flex-shrink-0 mt-0.5"
                          />
                          <span className="text-text-secondary text-sm leading-relaxed">
                            {detail}
                          </span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}